import { AgSelect } from 'src/components/ui';
import { DeferredRetry, DeferredSkeleton } from 'src/modules/entities/deferred';
import { useUpdateOrgSettings } from 'src/modules/entities/organization';
import type { InventoryPreferencesQuery, UnitRounding } from 'types/graphql';
import SettingsSection from '../../SettingsSection';
import { SetGS1CompanyPrefix } from './SetGS1CompanyPrefix';
import { useInventoryPreferences } from './useInventoryPreferences';

export default function InventoryPreferencesTab() {
  const { preferences, error, refetch } = useInventoryPreferences();

  if (preferences) return <LoadedInventoryPreferencesTab preferences={preferences} />;
  if (error) {
    return (
      <DeferredRetry message="Unable to load preferences." actionLabel="Retry preferences" onRetry={() => refetch()} />
    );
  }
  return <DeferredSkeleton rows={2} />;
}

const unitRoundingOptions = [
  { value: 'TWO_DECIMALS' as UnitRounding, label: '2 Decimals' },
  { value: 'NEAREST_QUARTER' as UnitRounding, label: 'Nearest Quarter (0.00, 0.25, 0.50, 0.75)' },
];

function LoadedInventoryPreferencesTab({ preferences }: { preferences: InventoryPreferencesQuery }) {
  const gs1CompanyPrefix = preferences.organization?.gs1CompanyPrefix;
  const orgSettings = preferences.orgSettings;
  const { update: updateOrgSettings } = useUpdateOrgSettings();

  return (
    <div className="card col">
      <SettingsSection
        label="Unit Rounding"
        subtitle="How inventory unit quantities are rounded throughout the system."
        divider
      >
        <AgSelect
          options={unitRoundingOptions}
          value={orgSettings?.unitRounding ?? 'TWO_DECIMALS'}
          onChange={(key) => {
            updateOrgSettings({ input: { unitRounding: key as UnitRounding } });
          }}
          className="max-w-[560px]"
        />
      </SettingsSection>

      <SettingsSection
        label="GS1 Company Prefix (GCP)"
        subtitle="If you're using 18-digit SSCC identifiers (also called GS1-128) for your pallet tags, set your GS1 assigned company prefix here."
      >
        <SetGS1CompanyPrefix gs1CompanyPrefix={gs1CompanyPrefix} />
      </SettingsSection>
    </div>
  );
}
